import React from "react";
import { salesApi } from "../../../services/reportsService";
import { useReportData } from "../useReportData";
import {
  AlertStrip,
  BarList,
  DonutChart,
  ErrorState,
  Funnel,
  ItemList,
  KpiGrid,
  Leaderboard,
  Panel,
  ReportSkeleton,
  Row,
  SeriesLegend,
  TrendChart,
  TablePanel,
  DistTable,
  TrendTable,
  cols,
  useReportContext,
} from "../ReportKit";
import {
  clientColumns,
  leadColumns,
  enquiryColumns,
  OPEN_ENQUIRY,
} from "../drillColumns";
import {
  PALETTE,
  buildKpis,
  formatDate,
  formatValue,
  num,
  toDist,
} from "../reportUtils";

const ICONS = {
  totalClients: "mdi:account-group-outline",
  newClients: "mdi:account-plus-outline",
  totalLeads: "mdi:bullseye-arrow",
  newLeads: "mdi:flash-outline",
  leadConversionRate: "mdi:swap-horizontal",
  totalEnquiries: "mdi:file-document-outline",
  approvedEnquiries: "mdi:check-decagram-outline",
  pendingEnquiries: "mdi:timer-sand",
  pipelineValue: "mdi:chart-timeline-variant",
  wonValue: "mdi:trophy-outline",
  avgDealSize: "mdi:cash-multiple",
  avgSalesCycleDays: "mdi:calendar-clock-outline",
  overdueFollowUps: "mdi:alert-circle-outline",
};

const COPY = {
  totalClients: ["All clients", "Everyone you work with"],
  newClients: ["New clients", "Added {p}"],
  totalLeads: ["All leads", "People who might buy"],
  newLeads: ["New leads", "Added {p}"],
  leadConversionRate: ["Lead success rate", "Leads that became clients {p}"],
  totalEnquiries: ["Enquiries", "Received {p}"],
  approvedEnquiries: ["Approved", "Enquiries that got a yes {p}"],
  pendingEnquiries: ["Waiting", "Enquiries waiting for an answer"],
  pipelineValue: ["Money in progress", "Value of enquiries still open"],
  wonValue: ["Money won", "Value of approved enquiries {p}"],
  avgDealSize: ["Average deal", "Money for each approved enquiry"],
  avgSalesCycleDays: ["Days to close", "Average days to get a yes"],
  overdueFollowUps: [
    "Late follow-ups",
    "Calls or visits that should be done by now",
  ],
};

const OverviewView = ({ params, setFilter, onLoaded, refreshKey }) => {
  const { view } = useReportContext();
  const { data, loading, error, reload } = useReportData(
    () => salesApi.overview(params),
    [JSON.stringify(params), refreshKey],
    onLoaded,
  );
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <ReportSkeleton />;

  const { meta = {}, charts = {}, lists = {} } = data;
  const currency = meta.currency || "INR";
  const gran = meta.granularity;
  const kpis = buildKpis(data.kpis).map((k) => ({
    ...k,
    icon: ICONS[k.key],
    label: COPY[k.key]?.[0] || k.label,
    desc: COPY[k.key]?.[1],
  }));
  const overdue = kpis.find((k) => k.key === "overdueFollowUps")?.value || 0;
  const stuck = lists.stuckEnquiries || [];

  const enquirySeries = [
    { key: "created", label: "Created", color: PALETTE[0], type: "area" },
    { key: "approved", label: "Approved", color: PALETTE[1], type: "area" },
    { key: "rejected", label: "Rejected", color: PALETTE[4], type: "area" },
  ];
  const growthSeries = [
    { key: "newClients", label: "New clients", color: PALETTE[0], type: "bar" },
    {
      key: "cumulativeClients",
      label: "Total clients",
      color: PALETTE[3],
      type: "line",
      axis: "right",
    },
  ];
  const leadSeries = [
    { key: "newLeads", label: "New leads", color: PALETTE[5], type: "bar" },
    {
      key: "convertedLeads",
      label: "Converted",
      color: PALETTE[1],
      type: "bar",
    },
  ];
  const wonSeries = [
    {
      key: "wonValue",
      label: "Won value",
      color: PALETTE[1],
      type: "area",
      unit: "CURRENCY",
    },
  ];

  const clientParams = {
    startDate: params.startDate,
    endDate: params.endDate,
    ownerId: params.ownerId,
    source: params.source,
    industry: params.industry,
  };
  const enqParams = {
    startDate: params.startDate,
    endDate: params.endDate,
    ownerId: params.ownerId,
  };
  const created = { createdFrom: params.startDate, createdTo: params.endDate };
  const clientsT = (title, how, p = {}, extra = {}) => ({
    modal: {
      title,
      how,
      columns: clientColumns(currency),
      fetcher: salesApi.clientTable,
      params: { ...clientParams, ...p },
      sort: "createdAt,desc",
      searchable: true,
      ...extra,
    },
  });
  const leadsT = (title, how, p = {}, extra = {}) => ({
    modal: {
      title,
      how,
      columns: leadColumns(),
      fetcher: salesApi.leadTable,
      params: { ...clientParams, ...p },
      sort: "createdAt,desc",
      searchable: true,
      ...extra,
    },
  });
  const enqT = (title, how, p = {}, extra = {}) => ({
    modal: {
      title,
      how,
      columns: enquiryColumns(currency),
      fetcher: salesApi.enquiryTable,
      params: { ...enqParams, ...p },
      sort: "createdAt,desc",
      searchable: true,
      ...extra,
    },
  });
  const details = {
    totalClients: clientsT(
      "All clients",
      "These are all the clients you can see.",
    ),
    newClients: clientsT("New clients", "These are the clients added {p}.", {
      isNew: true,
    }),
    totalLeads: leadsT("All leads", "These are all the leads you can see."),
    newLeads: leadsT("New leads", "These are the leads added {p}.", created),
    leadConversionRate: leadsT(
      "Leads who became clients",
      "Out of the leads added {p}, these became clients.",
      created,
      { filter: (r) => !!(r.convertedAt || r.convertedClientId) },
    ),
    totalEnquiries: enqT("Enquiries", "These are the enquiries received {p}."),
    approvedEnquiries: enqT(
      "Approved enquiries",
      "These enquiries got a yes {p}.",
      { status: "APPROVED" },
    ),
    pendingEnquiries: enqT(
      "Waiting enquiries",
      "These enquiries are waiting for an answer.",
      { status: "PENDING" },
    ),
    pipelineValue: enqT(
      "Money in progress",
      "These open enquiries add up to the money in progress.",
      {},
      { filter: OPEN_ENQUIRY },
    ),
    wonValue: enqT(
      "Money won",
      "These approved enquiries add up to the money won {p}.",
      { status: "APPROVED" },
    ),
    avgDealSize: enqT(
      "Approved enquiries",
      "Divide the money won by these enquiries to get the average deal.",
      { status: "APPROVED" },
    ),
    avgSalesCycleDays: enqT(
      "Approved enquiries",
      "Each took some days to get a yes. The average of those days is shown.",
      { status: "APPROVED" },
    ),
    overdueFollowUps: { scrollTo: "follow-ups" },
  };

  return (
    <div className={`rp-view ${loading ? "rp-refreshing" : ""}`}>
      <AlertStrip
        items={[
          overdue > 0 && {
            tone: "bad",
            icon: "mdi:alarm-light-outline",
            text: `${overdue} follow-ups are overdue`,
          },
          stuck.length > 0 && {
            tone: "warn",
            icon: "mdi:timer-alert-outline",
            text: `${stuck.length} enquiries have not moved stage recently`,
          },
        ]}
      />
      <KpiGrid kpis={kpis} currency={currency} details={details} />
      {view === "table" ? (
        <>
          <div className="rp-tables">
            <TrendTable
              title="Enquiries by date"
              data={charts.enquiryTrend}
              series={[...enquirySeries, ...wonSeries]}
              granularity={gran}
              currency={currency}
            />
            <TrendTable
              title="Clients by date"
              data={charts.clientGrowth}
              series={growthSeries}
              granularity={gran}
            />
            <TrendTable
              title="Leads by date"
              data={charts.leadsVsConverted}
              series={leadSeries}
              granularity={gran}
            />
            <TablePanel
              title="Pipeline stages"
              exportName="pipeline-stages"
              columns={[
                cols.text("stage", "Stage"),
                cols.num("count", "Enquiries"),
                cols.num("value", "Money", "CURRENCY", currency),
                cols.num("conversion", "From last stage %", "PERCENT"),
                cols.num("days", "Days in stage", "DECIMAL"),
              ]}
              rows={(charts.pipelineFunnel || []).map((s) => ({
                stage: s.stageName,
                count: s.count,
                value: num(s, "value"),
                conversion: num(s, "conversionFromPrevious"),
                days: num(s, "avgDaysInStage"),
              }))}
            />
            <DistTable
              title="Enquiry status"
              data={toDist(charts.enquiryStatus)}
              nameHeader="Status"
              valueHeader="Enquiries"
            />
            <DistTable
              title="Where leads come from"
              data={toDist(charts.leadSources)}
              nameHeader="Source"
              valueHeader="Leads"
            />
            <TablePanel
              title="Top performers"
              exportName="top-performers"
              columns={[
                cols.text("name", "Person"),
                cols.num("won", "Money won", "CURRENCY", currency),
                cols.num("pipeline", "Money open", "CURRENCY", currency),
                cols.num("rate", "Success %", "PERCENT"),
                cols.num("deals", "Deals"),
              ]}
              rows={(charts.topPerformers || []).map((p) => ({
                name: p.employeeName,
                won: p.metrics?.wonValue,
                pipeline: p.metrics?.pipelineValue,
                rate: p.metrics?.conversionRate,
                deals: p.metrics?.count,
              }))}
            />
            <TablePanel
              title="Top clients"
              exportName="top-clients"
              columns={[
                cols.text("name", "Client"),
                cols.num("won", "Money won", "CURRENCY", currency),
                cols.num("count", "Enquiries"),
              ]}
              rows={(charts.topClients || []).map((c) => ({
                name: c.clientName,
                won: c.wonValue,
                count: c.enquiryCount,
              }))}
            />
            <TablePanel
              title="Newly added clients"
              exportName="new-clients"
              columns={[
                cols.text("clientName", "Client"),
                cols.text("industry", "Industry"),
                cols.text("source", "Source"),
                cols.text("ownerName", "Owner"),
                cols.date("createdAt", "Added"),
              ]}
              rows={lists.recentClients || []}
            />
            <TablePanel
              title="Recent enquiries"
              exportName="recent-enquiries"
              columns={[
                cols.text("enquiryCode", "Enquiry"),
                cols.text("clientName", "Client"),
                cols.text("stageName", "Stage"),
                cols.badge("status", "Status"),
                cols.badge("priority", "Priority"),
                cols.date("createdAt", "Created"),
              ]}
              rows={lists.recentEnquiries || []}
            />
            <TablePanel
              id="follow-ups"
              title="Upcoming follow-ups"
              exportName="follow-ups"
              columns={[
                cols.text("relatedName", "Who"),
                cols.text("type", "What"),
                cols.text("ownerName", "Owner"),
                cols.date("dueAt", "Due", true),
              ]}
              rows={lists.upcomingFollowUps || []}
            />
            <TablePanel
              title="Stuck enquiries"
              exportName="stuck-enquiries"
              columns={[
                cols.text("enquiryCode", "Enquiry"),
                cols.text("clientName", "Client"),
                cols.text("stageName", "Stage"),
                cols.num("daysSinceStageChange", "Days stuck"),
              ]}
              rows={stuck}
            />
          </div>
        </>
      ) : (
        <>
          <Row layout="2-1">
            <Panel
              title="Enquiries over time"
              subtitle="Created, approved and rejected"
              actions={<SeriesLegend series={enquirySeries} />}
            >
              <TrendChart
                data={charts.enquiryTrend}
                series={enquirySeries}
                granularity={gran}
                currency={currency}
              />
            </Panel>
            <Panel title="Enquiry status" subtitle="Current split">
              <DonutChart data={toDist(charts.enquiryStatus)} />
            </Panel>
          </Row>
          <Row layout="1-1">
            <Panel
              title="Client growth"
              subtitle="New clients and running total"
              actions={<SeriesLegend series={growthSeries} />}
            >
              <TrendChart
                data={charts.clientGrowth}
                series={growthSeries}
                granularity={gran}
              />
            </Panel>
            <Panel
              title="Leads vs converted"
              actions={<SeriesLegend series={leadSeries} />}
            >
              <TrendChart
                data={charts.leadsVsConverted}
                series={leadSeries}
                granularity={gran}
              />
            </Panel>
          </Row>
          <Row layout="2-1">
            <Panel title="Won value" subtitle="Value of approved enquiries">
              <TrendChart
                data={charts.enquiryTrend}
                series={wonSeries}
                granularity={gran}
                currency={currency}
                height={240}
              />
            </Panel>
            <Panel title="Lead sources" subtitle="Select a source to filter">
              <DonutChart
                data={toDist(charts.leadSources)}
                onSelect={(v) => setFilter("source", v)}
              />
            </Panel>
          </Row>
          <Row layout="1-1">
            <Panel title="Sales pipeline" subtitle="Enquiries by stage">
              <Funnel
                currency={currency}
                stages={(charts.pipelineFunnel || []).map((s) => ({
                  name: s.stageName,
                  count: Number(s.count) || 0,
                  value: num(s, "value"),
                  conversion: num(s, "conversionFromPrevious"),
                  days: num(s, "avgDaysInStage"),
                }))}
              />
            </Panel>
            <Panel title="Top performers" subtitle="Ranked by won value">
              <Leaderboard
                primaryUnit="CURRENCY"
                primaryLabel="won"
                currency={currency}
                rows={(charts.topPerformers || []).map((p) => ({
                  name: p.employeeName,
                  avatarUrl: p.avatarUrl,
                  primary: Number(p.metrics?.wonValue) || 0,
                  metrics: [
                    {
                      label: "Pipeline",
                      value: num(p.metrics, "pipelineValue"),
                      unit: "CURRENCY",
                    },
                    {
                      label: "Conversion",
                      value: num(p.metrics, "conversionRate"),
                      unit: "PERCENT",
                    },
                    { label: "Deals", value: num(p.metrics, "count") },
                  ],
                }))}
              />
            </Panel>
          </Row>
          <Row layout="1-1">
            <Panel title="Top clients" subtitle="By won value">
              <BarList
                unit="CURRENCY"
                currency={currency}
                color={PALETTE[3]}
                data={(charts.topClients || []).map((c) => ({
                  name: c.clientName,
                  key: c.clientId,
                  value: Number(c.wonValue) || 0,
                }))}
              />
            </Panel>
            <Panel title="Newly added clients">
              <ItemList
                empty="No clients added in this period"
                items={(lists.recentClients || []).map((c) => ({
                  id: c.clientId,
                  title: c.clientName,
                  subtitle: [c.industry, c.source].filter(Boolean).join(", "),
                  right: `${c.ownerName || "Unassigned"}, ${formatDate(c.createdAt)}`,
                }))}
              />
            </Panel>
          </Row>
          <Row layout="1-1">
            <Panel title="Recent enquiries">
              <ItemList
                empty="No enquiries yet"
                items={(lists.recentEnquiries || []).map((e) => ({
                  id: e.enquiryCode,
                  title: e.enquiryCode,
                  subtitle: [e.clientName, e.stageName]
                    .filter(Boolean)
                    .join(", "),
                  badge: e.status,
                  right: formatDate(e.createdAt),
                }))}
              />
            </Panel>
            <Panel id="follow-ups" title="Upcoming follow-ups">
              <ItemList
                empty="No follow-ups scheduled"
                items={(lists.upcomingFollowUps || []).map((f, i) => ({
                  id: i,
                  title: f.relatedName,
                  subtitle: [f.type, f.ownerName].filter(Boolean).join(", "),
                  right: formatDate(f.dueAt, true),
                }))}
              />
            </Panel>
          </Row>
          <Panel title="Stuck enquiries" subtitle="No stage change for a while">
            <ItemList
              empty="Every open enquiry is moving"
              items={stuck.map((e) => ({
                id: e.enquiryId,
                title: e.enquiryCode,
                subtitle: [e.clientName, e.stageName]
                  .filter(Boolean)
                  .join(", "),
                right: `${formatValue(e.daysSinceStageChange)} days in stage`,
              }))}
            />
          </Panel>{" "}
        </>
      )}
    </div>
  );
};

export default OverviewView;
